import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';

export interface NavItem {
  to: string;
  label: string;
  icon: string;
}

export interface NavSection {
  heading: string;
  items: NavItem[];
}

/**
 * Sidebar shell — same skeleton as every other portal's ui/Shell.tsx, with
 * one difference: this app has no whole-app sign-in gate (see src/App.tsx).
 * The Shell itself always renders, because the Public Procurement tender
 * register (and the licence register / trade reference tables) are reachable
 * without signing in at all — only the "Switch user"/"Sign in" footer
 * control changes with persona state.
 */
export function Shell({ sections }: { sections: NavSection[] }) {
  const { persona, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="flex min-h-screen">
      <aside className="fixed inset-y-0 left-0 flex w-64 flex-col overflow-y-auto bg-ink-950">
        <div className="flex items-center gap-2.5 px-5 py-5">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-brand-600 text-sm font-black text-white">TI</div>
          <div>
            <div className="text-sm font-bold leading-tight text-white">One Gov Seychelles</div>
            <div className="text-[11px] text-slate-400">Trade, Investment &amp; Industry</div>
          </div>
        </div>
        <nav className="flex-1 space-y-4 px-3 py-2">
          {sections.map((section) => (
            <div key={section.heading}>
              <div className="px-3 pb-1 text-[10px] font-bold uppercase tracking-wider text-slate-500">{section.heading}</div>
              <div className="space-y-0.5">
                {section.items.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.to.split('/').length <= 2}
                    className={({ isActive }) =>
                      `flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium transition ${
                        isActive ? 'bg-brand-600 text-white' : 'text-slate-300 hover:bg-ink-800 hover:text-white'
                      }`
                    }
                  >
                    <span className="w-5 text-center">{item.icon}</span>
                    {item.label}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>
        <div className="border-t border-ink-800 px-5 py-4">
          {persona ? (
            <>
              <div className="text-sm font-semibold text-white">{persona.name}</div>
              <div className="text-[11px] leading-snug text-slate-400">{persona.title}</div>
              <div className="text-[11px] leading-snug text-slate-500">{persona.org}</div>
              {persona.actingForBusinessId ? (
                <div className="mt-1 text-[11px] leading-snug text-amber-400/90">Acting for {persona.actingForBusinessId}</div>
              ) : null}
              <button
                className="mt-3 w-full rounded-lg border border-ink-700 px-3 py-1.5 text-xs font-semibold text-slate-300 hover:bg-ink-800"
                onClick={() => {
                  logout();
                  navigate('/procurement/tenders');
                }}
              >
                Switch user
              </button>
            </>
          ) : (
            <>
              <div className="text-sm font-semibold text-white">Browsing publicly</div>
              <div className="text-[11px] leading-snug text-slate-400">Public registers need no sign-in. Applying, bidding, or staff actions do.</div>
              <button className="btn-primary mt-3 w-full !py-1.5 text-xs" onClick={() => navigate('/signin')}>
                Sign in
              </button>
            </>
          )}
        </div>
      </aside>
      <main className="ml-64 flex-1 px-8 py-7">
        <Outlet />
      </main>
    </div>
  );
}
